import { useState, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import Navbar from "../../components/PSTVue/Navbar2";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100", "translate-y-0");
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email";
    }
    if (!formData.message.trim()) errors.message = "Message is required";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    setSubmitError("");
    setSubmitSuccess(false);

    if (Object.keys(errors).length !== 0) return;
    setIsSubmitting(true);

    const apiUrl = `${process.env.REACT_APP_CONTACT_US_API_URL}/api/contact`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.company,
          message: formData.message,
          source: "MNR-PSTVue",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
        });
      } else {
        setSubmitError(data.message || "Failed to submit lead. Please try again.");
        console.error("API Error:", data);
      }
    } catch (error) {
      setSubmitError("An unexpected network error occurred.");
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#04254d] min-h-screen w-full text-white flex flex-col">
      <Navbar />
      <section
        id="contact"
        ref={sectionRef}
        className="opacity-0 mt-4 translate-y-10 transition-all duration-700 pt-16 pb-28 px-4"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">Get in Touch</h2>
          <p className="text-gray-400 mt-3">
            Ready to transform your business? Let's connect.
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-[#1f2937] text-white shadow-xl rounded-2xl p-8 border border-gray-700 max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <h3 className="text-xl font-semibold">Send Us a Message</h3>
            
            {submitSuccess && (
              <div className="p-3 bg-green-700/20 text-green-400 border border-green-600 rounded-lg">
                Thank you! We'll get back to you shortly.
              </div>
            )}

            {submitError && (
              <div className="p-3 bg-red-700/20 text-red-400 border border-red-600 rounded-lg">
                {submitError}
              </div>
            )}

            {/* Full Name */}
            <div>
              <label className="block font-medium">Full Name*</label>
              <input
                type="text"
                name="name"
                className={`w-full mt-1 px-4 py-2 bg-[#111827] border rounded-lg focus:ring ${
                  formErrors.name ? "border-red-500" : "border-gray-600"
                }`}
                value={formData.name}
                onChange={handleChange}
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Email Address*</label>
                <input
                  type="email"
                  name="email"
                  className={`w-full mt-1 px-4 py-2 bg-[#111827] border rounded-lg focus:ring ${
                    formErrors.email ? "border-red-500" : "border-gray-600"
                  }`}
                  value={formData.email}
                  onChange={handleChange}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block font-medium">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full mt-1 px-4 py-2 bg-[#111827] border border-gray-600 rounded-lg focus:ring"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label className="block font-medium">Company Name</label>
              <input
                type="text"
                name="company"
                className="w-full mt-1 px-4 py-2 bg-[#111827] border border-gray-600 rounded-lg focus:ring"
                value={formData.company}
                onChange={handleChange}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block font-medium">Your Message*</label>
              <textarea
                rows="5"
                name="message"
                className={`w-full mt-1 px-4 py-2 bg-[#111827] border rounded-lg focus:ring ${
                  formErrors.message ? "border-red-500" : "border-gray-600"
                }`}
                value={formData.message}
                onChange={handleChange}
              ></textarea>
              {formErrors.message && (
                <p className="text-red-500 text-sm mt-1">{formErrors.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition disabled:opacity-60"
            >
              {isSubmitting ? "Sending..." : "Send Message"} <Send size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;